import React, { useState, useEffect } from "react";
import { useApp } from "@/store/AppContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Check, AlertCircle } from "lucide-react";

const VISA_TYPES = ["Work", "Study", "Tourist", "Permanent Residency", "Business"];
const COUNTRIES = ["Canada", "United Kingdom", "Australia", "Germany", "UAE", "Singapore", "USA", "New Zealand", "Ireland", "Netherlands", "Other"];
const TIMELINES = ["Under 3 months", "3-6 months", "6-12 months", "1+ years"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[\d\s\-()]{8,20}$/; // permissive; allow +, digits, spaces, dashes, parens

function validate(f) {
  const errs = {};
  if (!f.firstName?.trim()) errs.firstName = "Required";
  if (!f.lastName?.trim()) errs.lastName = "Required";
  if (!f.email) errs.email = "Required";
  else if (!EMAIL_RE.test(f.email)) errs.email = "Enter a valid email address";
  if (!f.phone) errs.phone = "Required";
  else if (!PHONE_RE.test(f.phone)) errs.phone = "Include country code, digits only";
  else {
    const digits = f.phone.replace(/\D/g, "");
    if (digits.length < 8) errs.phone = "Phone number is too short";
  }
  if (!f.visa_type) errs.visa_type = "Please choose one";
  if (!f.target_country) errs.target_country = "Please choose one";
  if (!f.timeline) errs.timeline = "Please choose one";
  return errs;
}

export default function ContactForm() {
  const { contactOpen, setContactOpen, contactPrefill } = useApp();
  const [f, setF] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    visa_type: "", target_country: "", timeline: "", notes: "",
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (contactOpen) setF((cur) => ({ ...cur, ...contactPrefill }));
  }, [contactOpen, contactPrefill]);

  const errors = validate(f);
  const isValid = Object.keys(errors).length === 0;

  const close = () => {
    setContactOpen(false);
    setTimeout(() => {
      setF({ firstName: "", lastName: "", email: "", phone: "", visa_type: "", target_country: "", timeline: "", notes: "" });
      setTouched({});
    }, 300);
  };

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const blur = (k) => () => setTouched({ ...touched, [k]: true });

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, phone: true, visa_type: true, target_country: true, timeline: true });
    if (!isValid) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/leads", {
        name: `${f.firstName} ${f.lastName}`,
        email: f.email,
        phone: f.phone,
        visa_type: f.visa_type,
        target_country: f.target_country,
        timeline: f.timeline,
        notes: f.notes,
        transcript: [{ role: "system", content: "Submitted via Contact/Application form" }],
      });
      toast.success("Thanks! Check your inbox — a NextShore advisor will follow up within 24 hours.");
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (!contactOpen) return null;

  return (
    <div
      data-testid="contact-form-modal"
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl bg-app shadow-2xl border border-[var(--card-border)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-app z-10 flex items-center justify-between px-6 py-4 border-b border-[var(--card-border)]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-app-muted">Start your journey</div>
            <h2 className="font-serif text-2xl text-app">Tell us about you</h2>
          </div>
          <button data-testid="contact-form-close" onClick={close} className="text-app-muted hover:text-app">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *" error={touched.firstName && errors.firstName}>
              <input data-testid="cf-first" value={f.firstName} onChange={upd("firstName")} onBlur={blur("firstName")} className={input(touched.firstName && errors.firstName)} />
            </Field>
            <Field label="Last name *" error={touched.lastName && errors.lastName}>
              <input data-testid="cf-last" value={f.lastName} onChange={upd("lastName")} onBlur={blur("lastName")} className={input(touched.lastName && errors.lastName)} />
            </Field>
          </div>

          <Field label="Email *" error={touched.email && errors.email} valid={f.email && !errors.email}>
            <input type="email" data-testid="cf-email" value={f.email} onChange={upd("email")} onBlur={blur("email")} className={input(touched.email && errors.email, f.email && !errors.email)} placeholder="you@example.com" />
          </Field>

          <Field label="Phone (with country code) *" error={touched.phone && errors.phone} valid={f.phone && !errors.phone}>
            <input data-testid="cf-phone" value={f.phone} onChange={upd("phone")} onBlur={blur("phone")} className={input(touched.phone && errors.phone, f.phone && !errors.phone)} placeholder="+1 555 123 4567" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Visa type *" error={touched.visa_type && errors.visa_type}>
              <select data-testid="cf-visa" value={f.visa_type} onChange={upd("visa_type")} onBlur={blur("visa_type")} className={input(touched.visa_type && errors.visa_type)}>
                <option value="">Select…</option>
                {VISA_TYPES.map((v) => <option key={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Target country *" error={touched.target_country && errors.target_country}>
              <select data-testid="cf-country" value={f.target_country} onChange={upd("target_country")} onBlur={blur("target_country")} className={input(touched.target_country && errors.target_country)}>
                <option value="">Select…</option>
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Timeline *" error={touched.timeline && errors.timeline}>
            <select data-testid="cf-timeline" value={f.timeline} onChange={upd("timeline")} onBlur={blur("timeline")} className={input(touched.timeline && errors.timeline)}>
              <option value="">Select…</option>
              {TIMELINES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Notes (optional)">
            <textarea data-testid="cf-notes" value={f.notes} onChange={upd("notes")} rows={3} className={input() + " resize-none"} placeholder="Anything else we should know…" />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="cf-submit"
            className="w-full rounded-xl bg-[#0B1E3F] text-white font-semibold py-3 hover:bg-[#162A4D] disabled:opacity-60 transition"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
          <p className="text-[11px] text-app-muted text-center">
            By submitting, you agree to be contacted by a NextShore advisor.
          </p>
        </form>
      </div>

      <style>{`
        .cf-input {
          width: 100%;
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--card-border);
          border-radius: 12px;
          padding: 10px 14px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }
        .cf-input:focus { border-color: var(--accent); }
        .cf-input-error { border-color: #DC2626; }
        .cf-input-valid { border-color: #16A34A; }
      `}</style>
    </div>
  );
}

function input(err, valid) {
  return `cf-input ${err ? "cf-input-error" : valid ? "cf-input-valid" : ""}`;
}

function Field({ label, error, valid, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-app-muted flex items-center gap-1">
        {label}
        {valid && <Check className="w-3 h-3 text-emerald-600" />}
      </span>
      <div className="mt-1 relative">{children}</div>
      {error && (
        <span className="mt-1 text-[11px] text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </span>
      )}
    </label>
  );
}
